import React, { useCallback, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { Dropdown, Tag } from 'antd';
import type { MenuProps } from 'antd';

import { getAssetTypeName } from '@/shared/api/openmetadata';
import { SEMANTIC_DIMENSION } from '@/shared/design-tokens/design-tokens';
import { getAssetIconStyle, getAssetIconWrapperStyle } from '@/shared/lib/asset';
import { ROUTES } from '@/shared/lib/constants';
import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';

import { useTagDetail } from '@/entities/tags';

import { useDeleteTag, useTagCategoryDetail } from '@/features/tags/model';
import { TagFormModal } from '@/features/tags/ui/TagFormModal';

import styles from './TagDetailHeader.module.css';

type TagDetailHeaderProps = {
  selectedTagFqn?: string;
  onTagUpdated?: (parentFqn: string) => void;
};

export const TagDetailHeader = React.memo(({ selectedTagFqn, onTagUpdated }: TagDetailHeaderProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { data } = useTagDetail({ tagName: selectedTagFqn ?? '' });
  const { id, displayName, name, description, classification } = data;
  const { name: classificationName, displayName: classificationDisplayName } = classification;

  const { data: categoryData } = useTagCategoryDetail({ categoryName: classificationName });

  const { handleDelete, isPending: _isDeleting } = useDeleteTag(selectedTagFqn, {
    onTreeRefresh: onTagUpdated,
  });

  const selectedTagName = useMemo(() => {
    return displayName || name;
  }, [displayName, name]);

  const breadcrumbItems = useMemo(() => {
    return [
      {
        title: (
          <Link to={`${ROUTES.TAGS}/${classificationName}`} className={styles.breadcrumbItem}>
            <EllipsisText text={classificationDisplayName || classificationName} className={styles.breadcrumbText} />
          </Link>
        ),
      },
      {
        title: (
          <span className={styles.breadcrumbItem}>
            <EllipsisText text={selectedTagName} className={styles.breadcrumbText} />
          </span>
        ),
      },
    ];
  }, [classificationName, classificationDisplayName, selectedTagName]);

  const handleEditClick = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const handleEditSuccess = useCallback(
    (parentFqn: string) => {
      onTagUpdated?.(parentFqn);
    },
    [onTagUpdated]
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteExecute = useCallback(async () => {
    await handleDelete(id);
  }, [handleDelete, id]);

  const deleteMessage = useMemo(() => {
    return `"${selectedTagName}" 태그를 삭제하시겠습니까?`;
  }, [selectedTagName]);

  const dropdownItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'edit',
        icon: <Icon name="edit" size={16} className={styles.icon} />,
        label: '수정',
        onClick: handleEditClick,
      },
      {
        key: 'delete',
        icon: <DeleteOutlined className={styles.icon} />,
        label: '삭제',
        onClick: handleDeleteClick,
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  const parentInfo = useMemo(
    () => ({
      classification: classificationName,
      classificationName: classificationDisplayName || classificationName,
    }),
    [classificationName, classificationDisplayName]
  );

  const iconConfig = useMemo(() => {
    const { iconColor } = getAssetIconStyle('tag');
    return {
      component: <Icon name="tag" className="icon" color={iconColor} />,
      wrapperStyle: getAssetIconWrapperStyle('tag'),
      tooltip: getAssetTypeName('tag'),
    };
  }, []);

  const isMutuallyExclusive = categoryData?.mutuallyExclusive;

  return (
    <>
      <ContentHeader
        breadcrumbItems={breadcrumbItems}
        icon={iconConfig}
        title={selectedTagName}
        description={description}
        titleRowExtra={isMutuallyExclusive && <Tag style={{ height: SEMANTIC_DIMENSION.REGULAR }}>상호 배타적</Tag>}
        actions={
          <Dropdown
            menu={{ items: dropdownItems, className: styles.dropdownMenu }}
            trigger={['click']}
            placement="bottomRight"
            rootClassName={styles.dropdown}>
            <button type="button" className={styles.moreButton}>
              <MoreOutlined className={styles.icon} />
            </button>
          </Dropdown>
        }
      />

      <TagFormModal
        open={isEditModalOpen}
        mode="edit"
        parentInfo={parentInfo}
        tagData={data}
        tagFqn={selectedTagFqn}
        onClose={handleEditModalClose}
        onSuccess={handleEditSuccess}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        message={deleteMessage}
        confirmText="삭제"
        cancelText="취소"
        confirmButtonProps={{ danger: true }}
        onConfirm={handleDeleteExecute}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
});

TagDetailHeader.displayName = 'TagDetailHeader';
